import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Check, Pencil } from "lucide-react";

export default function AdminPlans() {
  const { t } = useT();
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // plan being edited
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/plan-limits").then((r) => setPlans(r.data.plans));
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      price: p.price,
      haat_limit: p.haat_limit,
      helper_limit: p.helper_limit,
      monthly_animal_limit: p.monthly_animal_limit || 0,
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/plan-limits/${editing.id}`, {
        price: Number(form.price),
        haat_limit: Number(form.haat_limit),
        helper_limit: Number(form.helper_limit),
        monthly_animal_limit: Number(form.monthly_animal_limit) || 0,
      });
      toast.success("Plan limits updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell title={t("nav.plans")} subtitle={t("admin.plansSubtitle")}>
      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p) => (
          <div key={p.id} className={`am-card p-7 ${p.highlight ? "ring-2 ring-primary border-primary" : ""}`} data-testid={`plan-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-2xl capitalize">{p.name}</h3>
              {p.highlight && <span className="am-chip"><Sparkles className="w-3 h-3" /> {t("admin.popular")}</span>}
            </div>
            <div className="mt-3 font-display text-4xl font-extrabold">
              ₹{p.price.toLocaleString("en-IN")}
              <span className="text-sm font-normal text-muted-foreground">{t("admin.month")}</span>
            </div>
            <ul className="mt-6 space-y-2.5">
              {(p.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t space-y-1 text-xs text-muted-foreground">
              <div>{t("admin.helperLimitLbl")}: <span className="font-mono font-bold text-foreground">{p.helper_limit >= 9999 ? t("admin.unlimited") : p.helper_limit}</span></div>
              <div>Pashu Haats: <span className="font-mono font-bold text-foreground">{p.haat_limit}</span></div>
              <div>Animal Registrations: <span className="font-mono font-bold text-foreground">{p.monthly_animal_limit ? `${p.monthly_animal_limit.toLocaleString("en-IN")} / month` : t("admin.unlimited")}</span></div>
              <div>Support: <span className="font-semibold text-foreground">{p.support_tier}</span></div>
            </div>
            <Button variant="outline" size="sm" className="rounded-full mt-4 w-full" data-testid={`edit-plan-${p.id}`} onClick={() => openEdit(p)}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit Limits
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="plan-limits-dialog">
          <DialogHeader>
            <DialogTitle className="font-display capitalize">Edit {editing?.name} Limits</DialogTitle>
            <DialogDescription>Changes apply immediately — no code change needed. Helper limit syncs to all subscribers on this plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ["price", "Monthly Price (₹)"],
              ["haat_limit", "Max Pashu Haats"],
              ["helper_limit", "Max Helpers"],
              ["monthly_animal_limit", "Animal Registrations / month (0 = unlimited)"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</label>
                <Input type="number" className="am-input mt-1" data-testid={`plan-limit-${k}`}
                  value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="plan-limits-save" className="am-btn-primary w-full" disabled={busy} onClick={save}>{busy ? "…" : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
